import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../utils/constant';

const initialState = {
  masters: [],
  loading: false,
  error: null,
};

// Fetch all dispensing masters (supports optional category filter)
export const fetchDispensingMasters = createAsyncThunk('dispensingMaster/fetchDispensingMasters', async (category) => {
  const params = category && category !== 'ALL' ? `?category=${category}` : '';
  const response = await axios.get(`${API_URL}/dispensing-master${params}`);
  return response.data;
});

// Create a new dispensing master
export const createDispensingMaster = createAsyncThunk('dispensingMaster/createDispensingMaster', async (masterData) => {
  const response = await axios.post(`${API_URL}/dispensing-master`, masterData);
  return response.data;
});

// Update a dispensing master
export const updateDispensingMaster = createAsyncThunk('dispensingMaster/updateDispensingMaster', async ({ id, masterData }) => {
  const response = await axios.put(`${API_URL}/dispensing-master/${id}`, masterData);
  return response.data;
});

// Delete a dispensing master
export const deleteDispensingMaster = createAsyncThunk('dispensingMaster/deleteDispensingMaster', async (id) => {
  await axios.delete(`${API_URL}/dispensing-master/${id}`);
  return id;
});

// Seed dispensing masters from Excel reference
export const seedDispensingMasters = createAsyncThunk('dispensingMaster/seedDispensingMasters', async () => {
  const response = await axios.post(`${API_URL}/dispensing-master/seed`);
  return response.data;
});

const dispensingMasterSlice = createSlice({
  name: 'dispensingMaster',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchDispensingMasters.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDispensingMasters.fulfilled, (state, action) => {
        state.loading = false;
        state.masters = Array.isArray(action.payload) ? action.payload : [];
        state.error = null;
      })
      .addCase(fetchDispensingMasters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Create
      .addCase(createDispensingMaster.pending, (state) => {
        state.loading = true;
      })
      .addCase(createDispensingMaster.fulfilled, (state, action) => {
        state.loading = false;
        state.masters.push(action.payload);
        state.error = null;
      })
      .addCase(createDispensingMaster.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Update
      .addCase(updateDispensingMaster.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateDispensingMaster.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.masters.findIndex(master => master.id === action.payload.id);
        if (index !== -1) {
          state.masters[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateDispensingMaster.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Delete
      .addCase(deleteDispensingMaster.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteDispensingMaster.fulfilled, (state, action) => {
        state.loading = false;
        state.masters = state.masters.filter(master => master.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteDispensingMaster.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Seed
      .addCase(seedDispensingMasters.pending, (state) => {
        state.loading = true;
      })
      .addCase(seedDispensingMasters.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(seedDispensingMasters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export default dispensingMasterSlice.reducer;
